import { Icon, Logo, SignedUrlForImage } from "components"
import { Image } from "antd-v5"
import { convertIOSDateToHTMLFormatTime } from "config/utils"
import tickMark from "images/tickMark.png"
import crossMark from "images/crossMark.png"
import viewOnMapMark from "images/viewOnMapMark.png"
import moment from "moment"
import { PreviousShiftEvent } from "@type/workMonitor.types"
import { EmployeeQualificationSiaInterface } from "@type/employees.types"
import Pill from "components/Pill"
import cx from "classnames"

interface Props {
	data: {
		title: string
		date: string
		customer: string
		address: string
		location: string
		employee: string
		post: string
		startTime: string
		finishTime: string
		employeeQualification: EmployeeQualificationSiaInterface
		bookOnEvent: PreviousShiftEvent[]
		bookOffEvent: PreviousShiftEvent[]
	}
}

const Header: React.FC<Props> = ({ data }) => {
	const {
		title,
		date,
		startTime,
		post,
		finishTime,
		customer,
		employee,
		employeeQualification,
		address,
		location,
		bookOnEvent,
		bookOffEvent,
	} = data

	return (
		<div className="h-full w-full space-y-5">
			<div>
				<p className="text-right text-4 font-normal text-[#101828]">
					Date: {date ? moment(date).format("YYYY/MM/DD") : "N/A"}
				</p>
				<div className="flex space-x-12">
					<Logo />
					<div className="space-y-3">
						<div className="text-2xl font-medium text-[#101828]">{title}</div>
						<div className="text-lg font-[600] text-[#101828]">{customer}</div>
						<div className="space-y-2 text-md font-normal text-[#667085]">
							<div>{location}</div>
							<div>{address}</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex space-x-3 text-sm">
				<table className="h-full w-full table-auto border-0.125 border-[#667085]">
					<thead>
						<tr className="w-full">
							<th
								colSpan={2}
								className="border-0.125 border-[#667085] bg-[#B2DDFF] py-3 text-center  font-medium text-[#101828]"
							>
								EMPLOYEE DETAILS
							</th>
						</tr>
					</thead>

					<tbody className="h-full w-full font-medium text-[#101828]">
						<tr>
							<td className="w-1/4 border-0.125 border-[#667085] bg-[#EFF8FF] py-3.5 text-center">
								EMPLOYEE
							</td>
							<td className="w-3/4 border-0.125 border-[#667085] pl-20">{employee}</td>
						</tr>

						<tr>
							<td className="w-1/4 border-0.125 border-[#667085] bg-[#EFF8FF] py-3.5 text-center">
								SIA#
							</td>
							<td className=" w-3/4 pl-20">
								<div className="flex items-center space-x-2">
									<div>{employeeQualification.siaNumber}</div>
									{employeeQualification.siaNumber !== "N/A" && (
										<Pill
											label={
												employeeQualification?.siaStatus === "Active" ? "Active" : "Inactive"
											}
											icon={
												<img
													className=""
													src={
														employeeQualification?.siaStatus === "Active"
															? tickMark
															: crossMark
													}
												/>
											}
											className={cx({
												"bg-[#ECFDF3] text-[#027A48]":
													employeeQualification?.siaStatus === "Active",
												"bg-[#FEF3F2] text-[#B42318]":
													employeeQualification?.siaStatus !== "Active",
											})}
										/>
									)}
								</div>
							</td>
						</tr>

						<tr>
							<td className="w-1/4 border-0.125 border-[#667085] bg-[#EFF8FF] py-3.5 text-center ">
								POST
							</td>
							<td className="w-3/4 border-0.125 border-[#667085] py-3.5 pl-20">{post}</td>
						</tr>
					</tbody>
				</table>

				<table className="h-full w-full table-auto border-0.125 border-[#667085]">
					<thead>
						<tr className="w-full">
							<th
								colSpan={5}
								className="border-0.125 border-[#667085] bg-[#B2DDFF] py-3 text-center font-medium text-[#101828]"
							>
								SHIFT DETAILS
							</th>
						</tr>
						<tr className="w-full  bg-[#EFF8FF] text-center text-[#101828]">
							<th colSpan={2} className="border-0.125 border-[#667085] py-1.25  font-medium">
								BOOKON
							</th>
							<th colSpan={2} className="border-0.125 border-[#667085] font-medium">
								BOOKOFF
							</th>
							<th className="border-0.125 border-[#667085] font-medium">Duration</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td className="h-24 w-1/5 border-0.125 border-[#667085] py-1 px-2">
								{bookOnEvent?.[0]?.images?.length ? (
									<SignedUrlForImage path={bookOnEvent[0].images[0]}>
										{(signedUrl: string, loading: boolean) => {
											return loading ? (
												<div className="flex justify-center">
													<Icon name="spinner" color="black" className="animate-spin " />
												</div>
											) : (
												<Image.PreviewGroup>
													<Image
														className="object-fill"
														width={100}
														height={100}
														key={signedUrl}
														src={signedUrl}
														alt="avatar"
													/>
												</Image.PreviewGroup>
											)
										}}
									</SignedUrlForImage>
								) : (
									<span className="py-9 text-center text-gray-500">No image</span>
								)}
							</td>

							<td>
								<div className="px-1">
									<span className="w-full font-normal text-[#101828]">
										{convertIOSDateToHTMLFormatTime(startTime)}
									</span>

									<Pill
										label="View on map"
										className="w-full border border-[#175CD3] bg-[#EFF8FF] font-normal text-[#175CD3]"
										iconPosition="right"
										icon={<img src={viewOnMapMark} alt="map" className="h-4 w-4" />}
										onClick={() => {
											if (bookOnEvent[0]?.geo?.coordinates?.length === 2) {
												const [lng, lat] = bookOnEvent[0].geo.coordinates
												window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank")
											}
										}}
									/>
								</div>
							</td>

							<td className="h-24 w-1/5 border-0.125 border-[#667085] py-1 px-2">
								{bookOffEvent?.[0]?.images?.length ? (
									<SignedUrlForImage path={bookOffEvent[0]?.images[0]}>
										{(signedUrl: string, loading: boolean) => {
											return loading ? (
												<div className="flex justify-center">
													<Icon name="spinner" color="black" className="animate-spin" />
												</div>
											) : (
												<Image.PreviewGroup>
													<Image
														width={100}
														height={100}
														key={signedUrl}
														src={signedUrl}
														alt="avatar"
													/>
												</Image.PreviewGroup>
											)
										}}
									</SignedUrlForImage>
								) : (
									<p className="text-center text-gray-500">No image</p>
								)}
							</td>

							<td>
								<div className="px-1">
									<span className="w-full font-normal text-[#101828]">
										{convertIOSDateToHTMLFormatTime(finishTime)}
									</span>
									<Pill
										label="View on map"
										iconPosition="right"
										className="gap-1.5 border border-[#175CD3] bg-[#EFF8FF] font-normal text-[#175CD3]"
										icon={<img src={viewOnMapMark} alt="map" className="h-4 w-4" />}
										onClick={() => {
											if (bookOffEvent[0]?.geo?.coordinates?.length === 2) {
												const [lng, lat] = bookOffEvent[0].geo.coordinates
												window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank")
											}
										}}
									/>
								</div>
							</td>

							<td className="border-0.125 border-[#667085]">
								<div className=" text-center text-[#101828]">
									{startTime && finishTime
										? `${moment
												.duration(moment(finishTime).diff(moment(startTime)))
												.hours()}h ${moment
												.duration(moment(finishTime).diff(moment(startTime)))
												.minutes()}m`
										: "N/A"}
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default Header
